import React from 'react';
import { motion } from 'framer-motion';
import { useNavigation } from '../NavigationContext';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Space+Grotesk:wght@400;500;600;700;800&display=swap');

  .cmmc-page * { box-sizing: border-box; margin: 0; padding: 0; }
  .cmmc-page { font-family: 'Inter', sans-serif; color: #0d1120; background: #ffffff; width: 100%; }

  /* ── Hero ── */
  .cmmc-hero { position: relative; padding: 72px 24px 100px; overflow: hidden; background: #03060f; }
  .cmmc-hero-grid-bg { position: absolute; inset: 0; background-image: linear-gradient(rgba(6,182,212,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(6,182,212,0.06) 1px, transparent 1px); background-size: 48px 48px; pointer-events: none; }
  .cmmc-hero-glow1 { position: absolute; top: -10%; right: -8%; width: 600px; height: 600px; border-radius: 50%; background: radial-gradient(circle, rgba(6,182,212,0.18), transparent 65%); pointer-events: none; }
  .cmmc-hero-glow2 { position: absolute; bottom: -15%; left: -10%; width: 500px; height: 500px; border-radius: 50%; background: radial-gradient(circle, rgba(30,77,140,0.25), transparent 65%); pointer-events: none; }
  .cmmc-hero-inner { max-width: 1200px; margin: 0 auto; position: relative; z-index: 1; display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: center; }
  @media (max-width: 900px) { .cmmc-hero-inner { grid-template-columns: 1fr; } .cmmc-hero-visual { display: none; } }

  .cmmc-back-btn { display: flex; width: fit-content; align-items: center; gap: 8px; font-size: 14px; font-weight: 600; color: #06b6d4; background: none; border: none; cursor: pointer; padding: 0; margin-bottom: 28px; transition: color 0.2s; }
  .cmmc-back-btn:hover { color: #22d3ee; }

  .cmmc-badge { display: inline-flex; align-items: center; gap: 10px; padding: 8px 18px; border-radius: 999px; background: rgba(6,182,212,0.1); border: 1px solid rgba(6,182,212,0.3); font-size: 12.5px; font-weight: 700; color: #06b6d4; margin-bottom: 28px; letter-spacing: 0.04em; text-transform: uppercase; }
  .cmmc-badge-pulse { width: 8px; height: 8px; border-radius: 50%; background: #06b6d4; animation: cmmc-blink 2s ease-in-out infinite; }
  @keyframes cmmc-blink { 0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(6,182,212,0.5); } 50% { opacity: 0.6; box-shadow: 0 0 0 6px rgba(6,182,212,0); } }

  .cmmc-hero h1 { font-family: 'Space Grotesk', sans-serif; font-size: clamp(2rem, 4vw, 3.2rem); font-weight: 800; line-height: 1.06; letter-spacing: -0.03em; color: #fff; margin-bottom: 24px; }
  .cmmc-gradient { background: linear-gradient(135deg, #06b6d4 0%, #3b82f6 50%, #8b5cf6 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
  .cmmc-hero-sub { font-size: 1.08rem; color: #94a3b8; line-height: 1.82; margin-bottom: 36px; }
  .cmmc-btn-group { display: flex; gap: 14px; flex-wrap: wrap; margin-bottom: 40px; }
  .cmmc-btn-primary { padding: 14px 28px; background: #06b6d4; color: #03060f; border: none; border-radius: 12px; font-size: 15px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: all 0.2s; }
  .cmmc-btn-primary:hover { background: #22d3ee; transform: translateY(-2px); box-shadow: 0 8px 28px rgba(6,182,212,0.4); }
  .cmmc-btn-secondary { padding: 14px 28px; background: rgba(255,255,255,0.05); color: #e2e8f0; border: 1px solid rgba(255,255,255,0.15); border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
  .cmmc-btn-secondary:hover { background: rgba(255,255,255,0.1); border-color: rgba(6,182,212,0.4); }

  .cmmc-trust-row { display: flex; gap: 20px; flex-wrap: wrap; }
  .cmmc-trust-item { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #64748b; font-weight: 500; }
  .cmmc-trust-check { width: 18px; height: 18px; background: rgba(6,182,212,0.15); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 9px; color: #06b6d4; flex-shrink: 0; }

  /* Hero visual — CMMC scorecard */
  .cmmc-hero-visual { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); border-radius: 24px; padding: 28px; backdrop-filter: blur(8px); }
  .cmmc-card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; padding-bottom: 16px; border-bottom: 1px solid rgba(255,255,255,0.07); }
  .cmmc-card-label { font-size: 11px; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 3px; }
  .cmmc-card-value { font-size: 15px; font-weight: 700; color: #fff; }
  .cmmc-readiness-tag { background: rgba(245,158,11,0.15); color: #fbbf24; font-size: 11px; font-weight: 600; padding: 4px 10px; border-radius: 999px; border: 1px solid rgba(245,158,11,0.25); }
  .cmmc-domain-row { display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; border-radius: 10px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); margin-bottom: 7px; }
  .cmmc-domain-left { display: flex; align-items: center; gap: 10px; }
  .cmmc-domain-icon { font-size: 15px; }
  .cmmc-domain-name { font-size: 12.5px; font-weight: 600; color: #cbd5e1; }
  .cmmc-domain-sub { font-size: 10.5px; color: #475569; margin-top: 1px; }
  .cmmc-status-pass { background: rgba(6,182,212,0.12); color: #06b6d4; font-size: 10.5px; font-weight: 600; padding: 3px 10px; border-radius: 999px; }
  .cmmc-status-gap { background: rgba(239,68,68,0.12); color: #f87171; font-size: 10.5px; font-weight: 600; padding: 3px 10px; border-radius: 999px; }
  .cmmc-status-partial { background: rgba(245,158,11,0.12); color: #fbbf24; font-size: 10.5px; font-weight: 600; padding: 3px 10px; border-radius: 999px; }
  .cmmc-score-wrap { margin-top: 14px; padding: 16px 18px; background: linear-gradient(135deg, rgba(6,182,212,0.15), rgba(59,130,246,0.12)); border: 1px solid rgba(6,182,212,0.2); border-radius: 14px; }
  .cmmc-score-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
  .cmmc-score-label { font-size: 11px; color: #64748b; font-weight: 600; }
  .cmmc-score-value { font-size: 1.6rem; font-weight: 800; color: #06b6d4; letter-spacing: -0.03em; }
  .cmmc-score-bar-track { background: rgba(255,255,255,0.08); border-radius: 999px; height: 6px; }
  .cmmc-score-bar-fill { background: linear-gradient(90deg, #06b6d4, #3b82f6); border-radius: 999px; height: 6px; width: 64%; }

  /* ── Stats bar ── */
  .cmmc-stats-bar { background: #f8fafc; border-top: 1px solid #f1f5f9; border-bottom: 1px solid #f1f5f9; }
  .cmmc-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); max-width: 1200px; margin: 0 auto; }
  @media (max-width: 768px) { .cmmc-stats-grid { grid-template-columns: 1fr 1fr; } }
  .cmmc-stat { padding: 36px 20px; text-align: center; border-right: 1px solid #f1f5f9; }
  .cmmc-stat:last-child { border-right: none; }
  @media (max-width: 768px) { .cmmc-stat:nth-child(2n) { border-right: none; } .cmmc-stat { border-bottom: 1px solid #f1f5f9; } }
  .cmmc-stat-num { font-family: 'Space Grotesk', sans-serif; font-size: 2.1rem; font-weight: 800; color: #0ea5e9; letter-spacing: -0.04em; line-height: 1; margin-bottom: 8px; }
  .cmmc-stat-label { font-size: 0.82rem; color: #64748b; font-weight: 500; line-height: 1.4; }

  /* ── Shared section layout ── */
  .cmmc-section { padding: 88px 24px; }
  .cmmc-container { max-width: 1200px; margin: 0 auto; }
  .cmmc-section-header { text-align: center; max-width: 720px; margin: 0 auto 60px; }
  .cmmc-section-header h2 { font-family: 'Space Grotesk', sans-serif; font-size: clamp(1.8rem, 3.2vw, 2.7rem); font-weight: 800; color: #0d1120; letter-spacing: -0.03em; margin-bottom: 14px; line-height: 1.15; }
  .cmmc-section-header p { font-size: 1.03rem; color: #64748b; line-height: 1.8; }
  .cmmc-kicker { font-size: 12px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #0ea5e9; margin-bottom: 12px; }

  /* ── CMMC Level cards ── */
  .cmmc-levels-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
  @media (max-width: 900px) { .cmmc-levels-grid { grid-template-columns: 1fr; } }
  .cmmc-level-card { border-radius: 22px; border: 1px solid; padding: 36px 30px; position: relative; overflow: hidden; transition: all 0.3s; }
  .cmmc-level-card:hover { transform: translateY(-4px); box-shadow: 0 20px 56px rgba(0,0,0,0.1); }
  .cmmc-level-card.l1 { background: #f0fdfe; border-color: #a5f3fc; }
  .cmmc-level-card.l2 { background: #eff6ff; border-color: #bfdbfe; }
  .cmmc-level-card.l3 { background: #03060f; border-color: rgba(139,92,246,0.35); }
  .cmmc-level-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px; border-radius: 22px 22px 0 0; }
  .cmmc-level-card.l1::before { background: linear-gradient(90deg, #06b6d4, #22d3ee); }
  .cmmc-level-card.l2::before { background: linear-gradient(90deg, #3b82f6, #60a5fa); }
  .cmmc-level-card.l3::before { background: linear-gradient(90deg, #8b5cf6, #a78bfa); }
  .cmmc-level-num { font-family: 'Space Grotesk', sans-serif; font-size: 0.75rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 8px; }
  .cmmc-level-card.l1 .cmmc-level-num { color: #06b6d4; }
  .cmmc-level-card.l2 .cmmc-level-num { color: #3b82f6; }
  .cmmc-level-card.l3 .cmmc-level-num { color: #a78bfa; }
  .cmmc-level-title { font-family: 'Space Grotesk', sans-serif; font-size: 1.35rem; font-weight: 800; margin-bottom: 6px; }
  .cmmc-level-card.l1 .cmmc-level-title { color: #0c4a6e; }
  .cmmc-level-card.l2 .cmmc-level-title { color: #1e3a8a; }
  .cmmc-level-card.l3 .cmmc-level-title { color: #fff; }
  .cmmc-level-subtitle { font-size: 0.82rem; font-weight: 600; margin-bottom: 16px; }
  .cmmc-level-card.l1 .cmmc-level-subtitle { color: #0e7490; }
  .cmmc-level-card.l2 .cmmc-level-subtitle { color: #1d4ed8; }
  .cmmc-level-card.l3 .cmmc-level-subtitle { color: #8b5cf6; }
  .cmmc-level-desc { font-size: 0.88rem; line-height: 1.72; margin-bottom: 22px; }
  .cmmc-level-card.l1 .cmmc-level-desc { color: #164e63; }
  .cmmc-level-card.l2 .cmmc-level-desc { color: #1e40af; }
  .cmmc-level-card.l3 .cmmc-level-desc { color: #94a3b8; }
  .cmmc-level-tag { display: inline-flex; align-items: center; gap: 6px; font-size: 11.5px; font-weight: 700; padding: 5px 13px; border-radius: 999px; margin-bottom: 20px; }
  .cmmc-level-card.l1 .cmmc-level-tag { background: rgba(6,182,212,0.12); color: #06b6d4; border: 1px solid rgba(6,182,212,0.3); }
  .cmmc-level-card.l2 .cmmc-level-tag { background: rgba(59,130,246,0.12); color: #3b82f6; border: 1px solid rgba(59,130,246,0.3); }
  .cmmc-level-card.l3 .cmmc-level-tag { background: rgba(139,92,246,0.12); color: #a78bfa; border: 1px solid rgba(139,92,246,0.3); }
  .cmmc-level-reqs { display: flex; flex-direction: column; gap: 8px; padding-top: 18px; border-top: 1px solid; }
  .cmmc-level-card.l1 .cmmc-level-reqs { border-color: rgba(6,182,212,0.2); }
  .cmmc-level-card.l2 .cmmc-level-reqs { border-color: rgba(59,130,246,0.2); }
  .cmmc-level-card.l3 .cmmc-level-reqs { border-color: rgba(255,255,255,0.07); }
  .cmmc-level-req { display: flex; align-items: center; gap: 8px; font-size: 12.5px; }
  .cmmc-level-card.l1 .cmmc-level-req { color: #164e63; }
  .cmmc-level-card.l2 .cmmc-level-req { color: #1e3a8a; }
  .cmmc-level-card.l3 .cmmc-level-req { color: #94a3b8; }
  .cmmc-req-dot { width: 16px; height: 16px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 8px; flex-shrink: 0; }
  .cmmc-level-card.l1 .cmmc-req-dot { background: rgba(6,182,212,0.15); color: #06b6d4; }
  .cmmc-level-card.l2 .cmmc-req-dot { background: rgba(59,130,246,0.15); color: #3b82f6; }
  .cmmc-level-card.l3 .cmmc-req-dot { background: rgba(139,92,246,0.15); color: #a78bfa; }

  /* ── Services dark section ── */
  .cmmc-services-bg { background: #03060f; position: relative; overflow: hidden; }
  .cmmc-services-glow { position: absolute; top: -10%; right: 10%; width: 500px; height: 500px; background: radial-gradient(circle, rgba(6,182,212,0.09), transparent 70%); pointer-events: none; }
  .cmmc-services-bg .cmmc-kicker { color: #06b6d4; }
  .cmmc-services-bg .cmmc-section-header h2 { color: #fff; }
  .cmmc-services-bg .cmmc-section-header p { color: #64748b; }
  .cmmc-services-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
  @media (max-width: 960px) { .cmmc-services-grid { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 580px) { .cmmc-services-grid { grid-template-columns: 1fr; } }
  .cmmc-service-card { background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07); border-radius: 18px; padding: 28px; transition: all 0.3s; }
  .cmmc-service-card:hover { border-color: rgba(6,182,212,0.3); background: rgba(6,182,212,0.04); transform: translateY(-2px); }
  .cmmc-service-icon { font-size: 26px; margin-bottom: 14px; }
  .cmmc-service-title { font-size: 1rem; font-weight: 700; color: #e2e8f0; margin-bottom: 10px; }
  .cmmc-service-desc { font-size: 0.87rem; color: #64748b; line-height: 1.7; margin-bottom: 14px; }
  .cmmc-service-tags { display: flex; flex-wrap: wrap; gap: 6px; }
  .cmmc-service-tag { background: rgba(6,182,212,0.08); border: 1px solid rgba(6,182,212,0.18); color: #06b6d4; font-size: 10.5px; font-weight: 600; padding: 3px 10px; border-radius: 999px; }

  /* ── Risk / Consequences section ── */
  .cmmc-risk-bg { background: #f8fafc; border-top: 1px solid #f1f5f9; border-bottom: 1px solid #f1f5f9; }
  .cmmc-risk-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: start; }
  @media (max-width: 900px) { .cmmc-risk-grid { grid-template-columns: 1fr; gap: 40px; } }
  .cmmc-risk-left h2 { font-family: 'Space Grotesk', sans-serif; font-size: clamp(1.7rem, 2.8vw, 2.4rem); font-weight: 800; color: #0d1120; letter-spacing: -0.03em; line-height: 1.2; margin-bottom: 16px; }
  .cmmc-risk-left p { font-size: 1rem; color: #64748b; line-height: 1.78; margin-bottom: 32px; }
  .cmmc-risk-items { display: flex; flex-direction: column; gap: 14px; }
  .cmmc-risk-item { display: flex; gap: 14px; padding: 18px 20px; background: #fff; border-radius: 14px; border: 1px solid #fee2e2; }
  .cmmc-risk-icon-wrap { width: 40px; height: 40px; border-radius: 10px; background: #fee2e2; display: flex; align-items: center; justify-content: center; font-size: 17px; flex-shrink: 0; }
  .cmmc-risk-title { font-size: 0.93rem; font-weight: 700; color: #991b1b; margin-bottom: 4px; }
  .cmmc-risk-desc { font-size: 0.85rem; color: #64748b; line-height: 1.6; }
  .cmmc-domains-list { display: flex; flex-direction: column; gap: 18px; }
  .cmmc-domain-item { display: flex; gap: 14px; }
  .cmmc-domain-icon-wrap { width: 42px; height: 42px; border-radius: 12px; background: #e0f9ff; display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; margin-top: 2px; }
  .cmmc-domain-item-title { font-size: 0.93rem; font-weight: 700; color: #0d1120; margin-bottom: 4px; }
  .cmmc-domain-item-desc { font-size: 0.85rem; color: #64748b; line-height: 1.65; }

  /* ── Process ── */
  .cmmc-process-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 24px; position: relative; }
  @media (max-width: 1000px) { .cmmc-process-grid { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 600px) { .cmmc-process-grid { grid-template-columns: 1fr; } }
  .cmmc-process-grid::before { content: ''; position: absolute; top: 26px; left: 10%; right: 10%; height: 2px; background: linear-gradient(90deg, #06b6d4, #3b82f6, #8b5cf6); z-index: 0; }
  @media (max-width: 1000px) { .cmmc-process-grid::before { display: none; } }
  .cmmc-process-step { text-align: center; position: relative; z-index: 1; }
  .cmmc-process-num { width: 52px; height: 52px; border-radius: 50%; background: #0d1120; color: #06b6d4; font-size: 0.95rem; font-weight: 800; display: flex; align-items: center; justify-content: center; margin: 0 auto 18px; border: 4px solid #f8fafc; box-shadow: 0 0 0 2px #06b6d4; }
  .cmmc-process-title { font-size: 0.9rem; font-weight: 700; color: #0d1120; margin-bottom: 8px; }
  .cmmc-process-desc { font-size: 0.83rem; color: #64748b; line-height: 1.65; }

  /* ── FAQ ── */
  .cmmc-faq-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
  @media (max-width: 768px) { .cmmc-faq-grid { grid-template-columns: 1fr; } }
  .cmmc-faq-item { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 16px; padding: 24px 26px; transition: all 0.2s; }
  .cmmc-faq-item:hover { border-color: #a5f3fc; background: #f0fdfe; }
  .cmmc-faq-q { font-size: 0.95rem; font-weight: 700; color: #0d1120; margin-bottom: 10px; display: flex; align-items: flex-start; gap: 10px; }
  .cmmc-faq-q-icon { color: #06b6d4; font-weight: 800; flex-shrink: 0; }
  .cmmc-faq-a { font-size: 0.87rem; color: #64748b; line-height: 1.72; padding-left: 20px; }

  /* ── CTA ── */
  .cmmc-cta-bg { background: #fff; border-top: 1px solid #f1f5f9; }
  .cmmc-cta-inner { background: linear-gradient(135deg, #03060f 0%, #0c1a36 50%, #03060f 100%); border-radius: 28px; padding: 80px 56px; text-align: center; position: relative; overflow: hidden; max-width: 960px; margin: 0 auto; }
  @media (max-width: 640px) { .cmmc-cta-inner { padding: 52px 28px; border-radius: 20px; } }
  .cmmc-cta-glow1 { position: absolute; top: -20%; right: -5%; width: 380px; height: 380px; background: radial-gradient(circle, rgba(6,182,212,0.16), transparent 65%); pointer-events: none; }
  .cmmc-cta-glow2 { position: absolute; bottom: -20%; left: -5%; width: 380px; height: 380px; background: radial-gradient(circle, rgba(139,92,246,0.12), transparent 65%); pointer-events: none; }
  .cmmc-cta-tag { display: inline-block; background: rgba(6,182,212,0.1); border: 1px solid rgba(6,182,212,0.25); border-radius: 999px; padding: 6px 16px; font-size: 12px; font-weight: 700; color: #06b6d4; letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 24px; position: relative; z-index: 1; }
  .cmmc-cta-inner h2 { font-family: 'Space Grotesk', sans-serif; font-size: clamp(1.9rem, 3.5vw, 2.8rem); font-weight: 800; color: #fff; letter-spacing: -0.03em; line-height: 1.12; margin-bottom: 18px; position: relative; z-index: 1; }
  .cmmc-cta-inner > p { font-size: 1.05rem; color: #94a3b8; max-width: 560px; margin: 0 auto 40px; line-height: 1.75; position: relative; z-index: 1; }
  .cmmc-cta-btns { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; position: relative; z-index: 1; margin-bottom: 32px; }
  .cmmc-cta-primary { padding: 15px 32px; background: #06b6d4; color: #03060f; border: none; border-radius: 12px; font-size: 15px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: all 0.2s; box-shadow: 0 4px 20px rgba(6,182,212,0.4); }
  .cmmc-cta-primary:hover { background: #22d3ee; transform: translateY(-2px); }
  .cmmc-cta-secondary { padding: 15px 32px; background: rgba(255,255,255,0.06); color: #cbd5e1; border: 1px solid rgba(255,255,255,0.15); border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
  .cmmc-cta-secondary:hover { background: rgba(255,255,255,0.12); color: #fff; }
  .cmmc-cta-reassurances { display: flex; gap: 24px; justify-content: center; flex-wrap: wrap; position: relative; z-index: 1; }
  .cmmc-cta-reassurance { display: flex; align-items: center; gap: 8px; font-size: 13px; color: rgba(255,255,255,0.55); font-weight: 500; }
  .cmmc-cta-check { width: 18px; height: 18px; background: rgba(6,182,212,0.15); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 9px; color: #06b6d4; flex-shrink: 0; }
`;

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
  transition: { duration: 0.55 },
};
const stagger = (i: number) => ({
  ...fadeUp,
  transition: { duration: 0.5, delay: i * 0.08 },
});

const domainRows = [
  { icon: '🔐', name: 'Access Control', sub: '17 practices (L1)', status: 'cmmc-status-pass', label: 'Covered' },
  { icon: '🛡️', name: 'Incident Response', sub: '3 practices (L2)', status: 'cmmc-status-gap', label: 'Gap Found' },
  { icon: '📡', name: 'Audit & Accountability', sub: '9 practices (L2)', status: 'cmmc-status-partial', label: 'Partial' },
  { icon: '🔑', name: 'Configuration Mgmt', sub: '9 practices (L2)', status: 'cmmc-status-pass', label: 'Covered' },
];

const levels = [
  {
    cls: 'l1',
    num: 'CMMC Level 1',
    title: 'Foundational',
    subtitle: '17 Practices · Annual Self-Assessment',
    tag: '⚡ Entry-Level DoD Access',
    desc: 'Level 1 establishes basic cyber hygiene for companies handling Federal Contract Information (FCI). All 17 practices map directly to FAR clause 52.204-21 and require an annual self-assessment.',
    reqs: [
      'Protect Federal Contract Information (FCI)',
      'Annual self-assessment — no C3PAO required',
      'Covers basic access control, identification, system protection',
      'Required for all DoD prime and subcontractors',
    ],
  },
  {
    cls: 'l2',
    num: 'CMMC Level 2',
    title: 'Advanced',
    subtitle: '110 Practices · Triennial C3PAO Assessment',
    tag: '🏛️ CUI Handling Required',
    desc: 'Level 2 aligns fully with NIST SP 800-171 and applies to contractors handling Controlled Unclassified Information (CUI). Most DoD contracts require at minimum Level 2 certification.',
    reqs: [
      'Protect Controlled Unclassified Information (CUI)',
      'Third-party assessment by a certified C3PAO',
      'Maps to all 110 NIST SP 800-171 Rev 2 practices',
      'Covers 14 CMMC domains in full',
    ],
  },
  {
    cls: 'l3',
    num: 'CMMC Level 3',
    title: 'Expert',
    subtitle: '110+ Practices · Government-Led Assessment',
    tag: '🔒 High-Value Defense Programs',
    desc: 'Level 3 is reserved for contractors supporting the most critical DoD programs. It builds on NIST SP 800-171 with additional practices from NIST SP 800-172, assessed directly by DCSA.',
    reqs: [
      'Designed for highest-priority DoD programs',
      'Government-led assessment by DCSA',
      'Includes NIST SP 800-172 advanced practices',
      'Required for programs with critical national security data',
    ],
  },
];

const services = [
  { icon: '🔍', title: 'CMMC Gap Analysis', desc: 'We evaluate your current security posture against all 110 NIST SP 800-171 practices and identify every gap that would prevent certification.', tags: ['110 Controls', 'NIST 800-171', 'Risk Scoring'] },
  { icon: '📋', title: 'System Security Plan (SSP)', desc: 'We draft the mandatory SSP documenting your environment, security boundaries, and how each CMMC practice is implemented — a required artifact for assessment.', tags: ['SSP Drafting', 'C3PAO Ready', 'All Domains'] },
  { icon: '📝', title: 'POAM Development', desc: 'Where gaps exist, we build a Plan of Action & Milestones (POA&M) with prioritized remediation steps, timelines, and owners — required by CMMC 2.0 rules.', tags: ['POA&M', 'Remediation Plan', 'Timeline Mgmt'] },
  { icon: '🛡️', title: 'Policy & Procedure Drafting', desc: 'CMMC requires documented policies for every applicable domain. We write all 14 domain policies, procedures, and evidence packages your assessor will review.', tags: ['14 Domains', 'Evidence Packages', 'Audit-Ready'] },
  { icon: '☁️', title: 'CUI Boundary & Architecture', desc: 'We identify where CUI flows in your environment and architect your IT boundary to minimize scope, reduce assessment complexity, and isolate sensitive data.', tags: ['CUI Scoping', 'IT Architecture', 'FedRAMP Aware'] },
  { icon: '📊', title: 'Pre-Assessment Readiness Review', desc: 'Before your formal C3PAO assessment, we conduct a mock audit to find remaining deficiencies and ensure you walk in fully prepared for a successful assessment.', tags: ['Mock Audit', 'C3PAO Prep', 'Score Estimation'] },
];

const risks = [
  { icon: '🚫', title: 'Contract Termination', desc: 'DoD contracting officers can terminate contracts for cause if contractors are found non-compliant with required CMMC levels.' },
  { icon: '⚖️', title: 'False Claims Act Exposure', desc: 'Self-certifying compliance when deficiencies exist carries civil fraud liability under the False Claims Act — with treble damages.' },
  { icon: '🔒', title: 'Loss of CUI Clearance', desc: "Failing to protect CUI can result in removal from the DoD supplier base and disqualification from future prime and subcontract opportunities." },
];

const domains = [
  { icon: '🔐', title: 'Access Control (AC)', desc: 'Limit system access to authorized users, processes, and devices. Enforce least privilege across all user accounts and system components.' },
  { icon: '🔎', title: 'Audit & Accountability (AU)', desc: 'Create, protect, and review audit logs of system events. Ensure user activity can be traced for incident response and forensic purposes.' },
  { icon: '⚙️', title: 'Configuration Management (CM)', desc: 'Establish baseline configurations, control changes, and restrict installation of unauthorized software on company systems.' },
  { icon: '🛡️', title: 'Incident Response (IR)', desc: 'Establish and maintain a documented incident response capability — including detection, reporting, containment, and recovery procedures.' },
  { icon: '🏗️', title: 'System & Communications Protection (SC)', desc: 'Monitor and control communications at external boundaries, implement architectural designs that support security, and protect CUI in transit.' },
  { icon: '👁️', title: 'System & Information Integrity (SI)', desc: 'Identify, report, and correct system flaws. Protect against malicious code and monitor system alerts in real time.' },
];

const processSteps = [
  { num: '01', title: 'Scoping & Discovery', desc: 'We identify all systems, users, and data flows that touch CUI to precisely define your CMMC assessment scope.' },
  { num: '02', title: 'Gap Analysis', desc: 'A detailed review of all 110 practices against your current controls, generating a scored compliance report.' },
  { num: '03', title: 'Remediation', desc: 'We close gaps with technical controls, policy drafting, and vendor integrations — in priority order by risk impact.' },
  { num: '04', title: 'Documentation', desc: 'SSP, POA&M, and all domain policies are finalized, formatted, and packaged to assessor standards.' },
  { num: '05', title: 'C3PAO Readiness', desc: 'A mock assessment verifies readiness. We remain on-call through your official C3PAO or DCSA evaluation.' },
];

const faqs = [
  { q: 'Who needs CMMC certification?', a: 'Any company — prime contractor or subcontractor — that handles DoD Federal Contract Information (FCI) or Controlled Unclassified Information (CUI) must achieve the CMMC level required in their contract.' },
  { q: 'What is the difference between CMMC 1.0 and 2.0?', a: 'CMMC 2.0 (the current version, effective Dec 2024) streamlined the model from 5 levels to 3, eliminated CMMC-unique practices, and aligns fully with NIST SP 800-171 for Level 2.' },
  { q: 'How long does CMMC certification take?', a: 'Achieving Level 2 certification typically takes 6–18 months depending on your starting posture. Companies with strong existing IT controls can move faster. We help you find the fastest path.' },
  { q: 'What is a C3PAO?', a: 'A Certified Third-Party Assessment Organization (C3PAO) is an authorized firm that conducts official CMMC Level 2 assessments. We prepare you to pass — we can also refer trusted C3PAOs.' },
  { q: 'Can I self-assess for CMMC Level 2?', a: 'Select Level 2 contracts allow self-assessment (for non-critical programs), but most require a C3PAO assessment. All Level 1 assessments are annual self-assessments submitted to SPRS.' },
  { q: 'What is SPRS and why does it matter?', a: 'The Supplier Performance Risk System (SPRS) is where DoD contractors submit their NIST SP 800-171 self-assessment scores. Contracting officers check SPRS scores before awarding contracts.' },
];

const CMMCCybersecurity: React.FC = () => {
  const { navigateTo } = useNavigation();

  return (
    <>
      <style>{styles}</style>
      <div className="cmmc-page">

        {/* ── Hero ── */}
        <section className="cmmc-hero">
          <div className="cmmc-hero-grid-bg" />
          <div className="cmmc-hero-glow1" />
          <div className="cmmc-hero-glow2" />
          <div className="cmmc-hero-inner">
            <motion.div {...fadeUp}>
              <button className="cmmc-back-btn" onClick={() => navigateTo('')}>← Back</button>
              <div className="cmmc-badge">
                <span className="cmmc-badge-pulse" />
                DoD Cybersecurity Compliance
              </div>
              <h1>
                Get CMMC Certified.<br />
                <span className="cmmc-gradient">Win More DoD Contracts.</span>
              </h1>
              <p className="cmmc-hero-sub">
                The Cybersecurity Maturity Model Certification (CMMC) is mandatory
                for every contractor and subcontractor in the DoD supply chain. We
                assess your gaps, close them, and get you assessment-ready — faster
                than doing it alone.
              </p>
              <div className="cmmc-btn-group">
                <button className="cmmc-btn-primary">Start My CMMC Gap Analysis →</button>
                <button className="cmmc-btn-secondary">View CMMC Levels</button>
              </div>
              <div className="cmmc-trust-row">
                {['All 3 CMMC levels', 'NIST SP 800-171 aligned', 'SSP & POA&M included', 'C3PAO prep support'].map((t, i) => (
                  <div key={i} className="cmmc-trust-item">
                    <span className="cmmc-trust-check">✓</span>
                    {t}
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Scorecard visual */}
            <motion.div className="cmmc-hero-visual" {...fadeUp} transition={{ duration: 0.6, delay: 0.15 }}>
              <div className="cmmc-card-header">
                <div>
                  <div className="cmmc-card-label">Your Company</div>
                  <div className="cmmc-card-value">CMMC Readiness Dashboard</div>
                </div>
                <span className="cmmc-readiness-tag">⚠ Assessment Gaps Found</span>
              </div>
              {domainRows.map((d, i) => (
                <div key={i} className="cmmc-domain-row">
                  <div className="cmmc-domain-left">
                    <span className="cmmc-domain-icon">{d.icon}</span>
                    <div>
                      <div className="cmmc-domain-name">{d.name}</div>
                      <div className="cmmc-domain-sub">{d.sub}</div>
                    </div>
                  </div>
                  <span className={d.status}>{d.label}</span>
                </div>
              ))}
              <div className="cmmc-score-wrap">
                <div className="cmmc-score-top">
                  <div className="cmmc-score-label">SPRS Score (Current)</div>
                  <div className="cmmc-score-value">64 / 110</div>
                </div>
                <div className="cmmc-score-bar-track">
                  <div className="cmmc-score-bar-fill" />
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ── Stats ── */}
        <section className="cmmc-stats-bar">
          <div className="cmmc-stats-grid">
            {[
              { num: '$400B+', label: 'Annual DoD Contract Spending' },
              { num: '300K+', label: 'Defense Contractors Affected' },
              { num: '110', label: 'NIST 800-171 Practices (Level 2)' },
              { num: '14', label: 'CMMC Domains Covered' },
            ].map((s, i) => (
              <motion.div key={i} className="cmmc-stat" {...stagger(i)}>
                <div className="cmmc-stat-num">{s.num}</div>
                <div className="cmmc-stat-label">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ── CMMC Levels ── */}
        <section className="cmmc-section">
          <div className="cmmc-container">
            <motion.div className="cmmc-section-header" {...fadeUp}>
              <div className="cmmc-kicker">CMMC 2.0 Framework</div>
              <h2>Three Levels of Cybersecurity Certification</h2>
              <p>
                CMMC 2.0 defines three maturity levels based on the sensitivity of
                the information you handle. We prepare companies for all three —
                starting with the level your contract requires.
              </p>
            </motion.div>
            <div className="cmmc-levels-grid">
              {levels.map((l, i) => (
                <motion.div key={i} className={`cmmc-level-card ${l.cls}`} {...stagger(i)}>
                  <div className="cmmc-level-num">{l.num}</div>
                  <div className="cmmc-level-title">{l.title}</div>
                  <div className="cmmc-level-subtitle">{l.subtitle}</div>
                  <div className="cmmc-level-tag">{l.tag}</div>
                  <div className="cmmc-level-desc">{l.desc}</div>
                  <div className="cmmc-level-reqs">
                    {l.reqs.map((r, j) => (
                      <div key={j} className="cmmc-level-req">
                        <div className="cmmc-req-dot">✓</div>
                        <span>{r}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Our Services (dark) ── */}
        <section className="cmmc-section cmmc-services-bg">
          <div className="cmmc-services-glow" />
          <div className="cmmc-container">
            <motion.div className="cmmc-section-header" {...fadeUp}>
              <div className="cmmc-kicker">What We Do</div>
              <h2>Full-Service CMMC Compliance Support</h2>
              <p>
                From your first gap analysis to walking out of your C3PAO
                assessment with a passing score — we handle every deliverable
                the CMMC process requires.
              </p>
            </motion.div>
            <div className="cmmc-services-grid">
              {services.map((s, i) => (
                <motion.div key={i} className="cmmc-service-card" {...stagger(i)}>
                  <div className="cmmc-service-icon">{s.icon}</div>
                  <div className="cmmc-service-title">{s.title}</div>
                  <div className="cmmc-service-desc">{s.desc}</div>
                  <div className="cmmc-service-tags">
                    {s.tags.map((tag, j) => (
                      <span key={j} className="cmmc-service-tag">{tag}</span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Risks + Domains ── */}
        <section className="cmmc-section cmmc-risk-bg">
          <div className="cmmc-container">
            <div className="cmmc-risk-grid">
              <motion.div {...fadeUp}>
                <div className="cmmc-kicker">The Cost of Non-Compliance</div>
                <h2>Non-Compliance Isn't Just a Fine — It's Contract Loss</h2>
                <p>
                  Since CMMC became a contractual requirement, DoD contracting
                  officers actively verify compliance before award. Here's what's
                  at risk if you aren't certified.
                </p>
                <div className="cmmc-risk-items">
                  {risks.map((r, i) => (
                    <div key={i} className="cmmc-risk-item">
                      <div className="cmmc-risk-icon-wrap">{r.icon}</div>
                      <div>
                        <div className="cmmc-risk-title">{r.title}</div>
                        <div className="cmmc-risk-desc">{r.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
              <motion.div {...fadeUp} transition={{ duration: 0.55, delay: 0.12 }}>
                <div className="cmmc-kicker">Key CMMC Domains</div>
                <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 'clamp(1.5rem, 2.5vw, 2rem)', fontWeight: 800, color: '#0d1120', letterSpacing: '-0.03em', lineHeight: 1.2, marginBottom: '28px' }}>
                  14 Practice Domains, All Covered
                </div>
                <div className="cmmc-domains-list">
                  {domains.map((d, i) => (
                    <div key={i} className="cmmc-domain-item">
                      <div className="cmmc-domain-icon-wrap">{d.icon}</div>
                      <div>
                        <div className="cmmc-domain-item-title">{d.title}</div>
                        <div className="cmmc-domain-item-desc">{d.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ── Process ── */}
        <section className="cmmc-section">
          <div className="cmmc-container">
            <motion.div className="cmmc-section-header" {...fadeUp}>
              <div className="cmmc-kicker">How It Works</div>
              <h2>From Gap to Certified in 5 Steps</h2>
              <p>Our structured engagement takes you from current state to a passing assessment score — with every required artifact delivered along the way.</p>
            </motion.div>
            <div className="cmmc-process-grid">
              {processSteps.map((step, i) => (
                <motion.div key={i} className="cmmc-process-step" {...stagger(i)}>
                  <div className="cmmc-process-num">{step.num}</div>
                  <div className="cmmc-process-title">{step.title}</div>
                  <div className="cmmc-process-desc">{step.desc}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="cmmc-section" style={{ background: '#f8fafc', borderTop: '1px solid #f1f5f9' }}>
          <div className="cmmc-container">
            <motion.div className="cmmc-section-header" {...fadeUp}>
              <div className="cmmc-kicker">Common Questions</div>
              <h2>CMMC Questions, Answered</h2>
              <p>Everything defense contractors need to understand about CMMC 2.0 requirements, timelines, and the certification process.</p>
            </motion.div>
            <div className="cmmc-faq-grid">
              {faqs.map((faq, i) => (
                <motion.div key={i} className="cmmc-faq-item" {...stagger(i)}>
                  <div className="cmmc-faq-q">
                    <span className="cmmc-faq-q-icon">Q</span>
                    {faq.q}
                  </div>
                  <div className="cmmc-faq-a">{faq.a}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="cmmc-section cmmc-cta-bg">
          <div className="cmmc-container">
            <motion.div className="cmmc-cta-inner" {...fadeUp}>
              <div className="cmmc-cta-glow1" />
              <div className="cmmc-cta-glow2" />
              <div className="cmmc-cta-tag">Free CMMC Gap Analysis</div>
              <h2>Find Out Where You Stand Before Your Contracting Officer Does</h2>
              <p>
                Get a free CMMC gap analysis and receive a scored report against all
                applicable NIST SP 800-171 practices — with a clear remediation roadmap
                and SPRS score estimate.
              </p>
              <div className="cmmc-cta-btns">
                <button className="cmmc-cta-primary">Start My Free Gap Analysis →</button>
                <button className="cmmc-cta-secondary">Talk to a CMMC Specialist</button>
              </div>
              <div className="cmmc-cta-reassurances">
                {['No obligation', 'All 110 practices reviewed', 'SPRS score estimate included', 'Results in 5 business days'].map((r, i) => (
                  <div key={i} className="cmmc-cta-reassurance">
                    <span className="cmmc-cta-check">✓</span>
                    {r}
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

      </div>
    </>
  );
};

export default CMMCCybersecurity;
